<template>
  <activity-header
    :activity="currentActivities"
    :is-search-active="searchFieldOpen"
    :options="dealActivityOptions"
    :business-legal-name="activeDeal.business?.business_legal_name"
    :is-deals-list="isDealsList"
    :is-email-thread-open="!!activeEmailThread"
    :loading="anyLoading"
    @search:toggle="toggleSearchField"
    @minimize:toggle="toggleMinimized"
    @popover:init="initPopover"
    @pip:activate="isPiPModeActionTriggered = true"
    @activity:close="closeActivty"
    @activity-type:changed="handleActivityTypeChange"
  />
  <div
    id="deal-activity-main-body"
    class="flex flex-col items-stretch space-y-2"
    :class="[
      isShowingDraggable ? 'h-full max-h-full pb-14' : 'content-height',
      {
        invisible: dealActivityOptions.minimized && isShowingDraggable,
      },
    ]"
  >
    <lf-content-loader
      v-if="searchFieldOpen || isNoteOnlyActivity"
      :is-loading="anyLoading"
    >
      <search-input
        v-if="searchFieldOpen"
        v-model="searchString"
        class="pr-4"
        clear-button-enabled
        focus-on-mount
      />
      <template v-if="isNoteOnlyActivity">
        <deal-notes-input
          v-if="!searchFieldOpen"
          class="deal-notes-input z-101 mx-4"
          :note-to-edit="noteToEdit"
          :note-type="noteType"
          @edit-note:unset="setEditNote(null)"
          @type:update="updateNoteType"
          @note:edited="handleNotesUpdate"
        />
        <note-filters
          class="px-4 pb-0-5"
          @tab:changed="noteTabFilter = $event"
          @type:changed="noteTypeFilter = $event"
        />
      </template>
    </lf-content-loader>
    <lf-content-loader
      id="activity-list"
      ref="container"
      :is-loading="anyLoading"
      class="overflow-y-auto bg-white"
    >
      <component
        :is="currentActivities.logsComponent"
        v-if="currentActivities.total"
        v-bind="currentActivities.logComponentProps"
        @edit-note:set="setEditNote($event)"
        @note:pin-changed="getActivity"
        @thread:open="handleThreadOpen"
      />
      <component
        :is="currentActivities.emptyStateComponent"
        v-else
        v-bind="currentActivities.emptyStateComponentProps"
        class="mx-4"
      />
    </lf-content-loader>
  </div>
</template>
<script setup lang="ts">
import { useInfiniteScroll } from "@vueuse/core";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRaw,
  toRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { CommunicationType, NoteTab } from "@/enums/communicationLogs";
import {
  DealActivityState,
  FullNotableType,
  NoteType,
} from "@/enums/dealActivity";
import { FullStoryEvent } from "@/enums/events";
import { communicationTitle, isNoteLog } from "@/helpers/communication";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { COMMUNICATION_LOGS_APPLICATION_CHANNEL } from "@/helpers/websockets/common";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useFullStory } from "@/hooks/fullStory";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import {
  useCommunicationLogsQuery,
  useEmailThreadQuery,
} from "@/hooks/queries/useInbox";
import { ROUTE_DEALS } from "@/router/routes";
import notesService from "@/services/modules/notes";
import { useCommunicationStore } from "@/stores/communication";
import useNotesStore from "@/stores/notes";
import usePiPStore from "@/stores/pip";

import ActivityBody from "@/components/deals/dealActivity/ActivityBody.vue";
import ActivityHeader from "@/components/deals/dealActivity/ActivityHeader.vue";
import EmptyData from "@/components/deals/dealActivity/EmptyData.vue";
import EmailThreadBody from "@/components/deals/dealActivity/emails/EmailThreadBody.vue";
import EmptyNoteData from "@/components/deals/dealActivity/notes/EmptyNoteData.vue";
import NoteActivityBody from "@/components/deals/dealActivity/notes/NoteActivityBody.vue";
import NoteFilters from "@/components/deals/dealActivity/notes/NoteFilters.vue";
import LfContentLoader from "@/components/ui/LfContentLoader.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import DealNotesInput from "@/views/deals/components/dealNotes/DealNotesInput.vue";

import type { EmailLog, NoteLogData } from "@/models/communicationLogs";
import type { Note } from "@/models/notes";
import type { IUser } from "@/models/users";

const DISABLED_THREAD_ID = 0;

const { getters, dispatch, commit } = useStore();
const notesStore = useNotesStore();
const pipStore = usePiPStore();
const communicationStore = useCommunicationStore();
const { publishEvent } = useFullStory();
const container = ref<HTMLDivElement | null>(null);
const updateOrCreateNoteRequestLoading = ref(false);

const { activeDeal: deal } = useDeals();
const route = useRoute();
const {
  activeNoteDealId,
  activeNoteBusinessDealId,
  isDealActivityPipModeActivated,
} = storeToRefs(notesStore);
const { activePiPDeal } = storeToRefs(pipStore);
const { showMessage } = useNotification();
const { t } = useI18n();
const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);
const { hasNewLogs, dealActivityActiveTypes: activeTypes } =
  storeToRefs(communicationStore);

const isPiPModeActionTriggered = ref(false);
const searchFieldOpen = ref(false);
const noteType = ref<NoteType>(NoteType.Application);
const noteToEdit = ref<Note | null>(null);
const searchString = ref("");
const noteTypeFilter = ref<NoteType>();
const noteTabFilter = ref<NoteTab>(NoteTab.All);
const notePinnedFilterValue = ref<number>();
const noteRecentFilter = ref<string[]>();
const noteRequestTimeout = ref<number>();
const activeEmailThread = ref<EmailLog | null>(null);

useInfiniteScroll(
  container,
  () => {
    if (activeEmailThread.value) {
      if (!isemailThreadLoading.value && !isFetchingEmailThreadNextPage.value) {
        fetchEmailThreadNextPage();
      }
      return;
    }

    if (!isLoading.value && !isFetchingNextPage.value) {
      fetchNextPage();
    }
  },
  {
    distance: 100,
    canLoadMore: () =>
      activeEmailThread.value
        ? hasNextEmailThreadPage.value
        : hasNextPage.value,
  }
);

const logs = computed(
  () => data.value?.pages.flatMap((page) => page.data) || []
);
const meta = computed(() => data.value?.pages[0].meta);

const dealNotes = computed(() =>
  logs.value.reduce((acc: NoteLogData[], log) => {
    if (isNoteLog(log)) {
      acc.push(log.data);
    }
    return acc;
  }, [])
);

const isNoteOnlyActivity = computed(() => {
  return (
    activeTypes.value.length === 1 &&
    activeTypes.value[0] === CommunicationType.note
  );
});

const currentActivities = computed(() => {
  if (isNoteOnlyActivity.value) {
    return {
      total: meta.value?.total ?? 0,
      types: [CommunicationType.note],
      title: t("COMMON.NOTE", 2),
      logsComponent: NoteActivityBody,
      emptyStateComponent: EmptyNoteData,
      logComponentProps: {
        logs: notes.value,
        noteToEdit: noteToEdit.value,
      },
      emptyStateComponentProps: {
        noteType: noteType.value,
      },
    };
  }

  if (activeEmailThread.value) {
    return {
      total: meta.value?.total ?? 0,
      types: activeTypes.value,
      title: activeEmailThread.value?.data?.subject ?? "",
      logsComponent: EmailThreadBody,
      emptyStateComponent: EmptyData,
      logComponentProps: {
        log: activeEmailThread.value,
        threadEmails:
          emailThreadItems.value?.pages?.flatMap((page) => page.data) || [],
      },
      emptyStateComponentProps: {
        noteType: noteType.value,
      },
    };
  }

  const title = activeTypes.value
    .map((type) => communicationTitle[type])
    .join(", ");

  return {
    total: meta.value?.total ?? 0,
    types: activeTypes.value,
    title,
    logsComponent: ActivityBody,
    emptyStateComponent: EmptyData,
    logComponentProps: {
      logs: logs.value,
    },
    emptyStateComponentProps: {
      noteType: noteType.value,
    },
  };
});

const activeDeal = computed(() => activePiPDeal.value ?? deal.value);

const isDealsList = computed(() => route.name === ROUTE_DEALS);

/**
 * inside a deal, activeDeal should be responsible for the id;
 * in the deals list, active note deal id is returned
 */
const activeDealId = computed(() =>
  isDealsList.value ? (activeNoteDealId.value ?? "") : activeDeal.value.id
);

const isEmailThreaded = computed(
  () => activeTypes.value[0] === CommunicationType.email
);

const {
  data,
  isLoading,
  isPending,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  refetch: refetchActivity,
} = useCommunicationLogsQuery(
  toRef({
    application_ids: [activeDealId.value],
    type: activeTypes,
    search: searchString,
    note_type: noteTypeFilter,
    note_pinner: notePinnedFilterValue,
    note_created_at: noteRecentFilter,
  }),
  isEmailThreaded
);

const emailThreadId = computed(
  () => activeEmailThread.value?.data.email_thread_id ?? DISABLED_THREAD_ID
);

const {
  data: emailThreadItems,
  isLoading: isemailThreadLoading,
  isFetchingNextPage: isFetchingEmailThreadNextPage,
  fetchNextPage: fetchEmailThreadNextPage,
  hasNextPage: hasNextEmailThreadPage,
} = useEmailThreadQuery(emailThreadId);

const unreadNotesIds = computed(() =>
  notes.value["unread"].map((note) => note?.id as number)
);

const { fetchWrapper: handleNotesUpdate } = usePromiseWrapper(async () => {
  updateOrCreateNoteRequestLoading.value = true;
  /**
   * The loading should be finished once reindexing is done.
   * In rare cases reindexing takes longer than expected,
   * so we don't want to block the user from performing actions
   * with notes.
   */
  noteRequestTimeout.value = window.setTimeout(getActivity, 7_000);

  if (unreadNotesIds.value.filter(Boolean).length) {
    try {
      await notesService.markNotesAsRead(unreadNotesIds.value);
    } catch (error) {
      showMessage(t("ERRORS.MARK_NOTES_AS_READ_ERROR"), "error");
    }
  }
});

const { loading: requestLoading, fetchWrapper: getActivity } =
  usePromiseWrapper(
    async () => {
      if (isDealsList.value && !activeDealId.value) {
        return;
      }

      await refetchActivity();
    },
    {
      onError: () => showMessage(t("COMMON.ACCESS_RESTRICTED"), "error"),
      finally: () => {
        updateOrCreateNoteRequestLoading.value = false;
        clearTimeout(noteRequestTimeout.value);
      },
    }
  );

const { loading: workflowLoading, fetchWrapper: getDealDataAndNotes } =
  usePromiseWrapper(async (id: string) => {
    await Promise.all([
      dispatch("applications/getApplication", id),
      dispatch("workflows/getWorkflowSnapshot", id),
    ]);
  });

const user = computed<IUser>(() => getters["auth/user"]);

const anyLoading = computed(
  () =>
    requestLoading.value ||
    isPending.value ||
    isemailThreadLoading.value ||
    workflowLoading.value ||
    updateOrCreateNoteRequestLoading.value
);
const isShowingDraggable = computed(
  () => dealActivityOptions.value.state === DealActivityState.Draggable
);

const notes = computed(() => {
  const userId = user.value?.id;
  const read: NoteLogData[] = [];
  const unread: NoteLogData[] = [];

  for (const record of dealNotes.value) {
    if (!record) {
      continue;
    }

    const readNotesUserIds =
      record.read_notes?.reduce((acc, note) => {
        if (note.user?.id) {
          acc.add(note.user.id);
        }
        return acc;
      }, new Set<number>()) || new Set<number>();

    const isRead = readNotesUserIds.has(userId);
    const recordGroup = isRead ? read : unread;
    recordGroup.push(record);
  }

  const sortDesc = (a: NoteLogData, b: NoteLogData) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

  return {
    read: read.sort(sortDesc),
    unread: unread.sort(sortDesc),
  };
});

const handleThreadOpen = (emailLog: EmailLog) => {
  activeEmailThread.value = emailLog;
  publishEvent(FullStoryEvent.FS_DEAL_EMAIL_THREAD_OPENED);
};

const closeActivty = () => {
  if (unreadNotesIds.value.length) {
    notesService.markNotesAsRead(unreadNotesIds.value);
  }
  dealActivityOptions.value = {
    ...dealActivityOptions.value,
    state: DealActivityState.Closed,
  };
  activeNoteDealId.value = null;
  activeTypes.value = [CommunicationType.note];
  activeEmailThread.value = null;
};

const toggleMinimized = () => {
  dealActivityOptions.value = {
    ...dealActivityOptions.value,
    minimized: !dealActivityOptions.value.minimized,
  };
};

const closeSearchInput = () => {
  searchString.value = "";
  searchFieldOpen.value = false;
};

const initPopover = () => {
  dealActivityOptions.value = {
    ...dealActivityOptions.value,
    state:
      dealActivityOptions.value.state === DealActivityState.Draggable
        ? DealActivityState.Static
        : DealActivityState.Draggable,
  };
};

const handleActivityTypeChange = (types: CommunicationType[]) => {
  activeTypes.value = types;
  activeEmailThread.value = null;
  publishEvent(FullStoryEvent.FS_DEAL_ACTIVITY_TYPE_CHANGED, {
    type: types[0],
  });
};

const setEditNote = (note: Note | null) => {
  searchFieldOpen.value = false;

  nextTick(() => {
    noteToEdit.value = note;
  });

  if (!note) {
    return;
  }

  const notableType = note.notable_type;

  if (notableType === FullNotableType.business) {
    noteType.value = NoteType.Business;
  } else if (notableType === FullNotableType.call) {
    noteType.value = NoteType.Call;
  } else {
    noteType.value = NoteType.Application;
  }
};

const toggleSearchField = () => {
  if (searchFieldOpen.value) {
    closeSearchInput();
    return;
  }
  searchFieldOpen.value = true;

  if (dealActivityOptions.value.minimized) {
    dealActivityOptions.value = {
      ...dealActivityOptions.value,
      minimized: false,
    };
  }
};

const updateNoteType = (type: NoteType) => {
  noteType.value = type;
  activeNoteBusinessDealId.value =
    type === NoteType.Business ? activeDeal.value.business?.id : null;
};

const setRecentFilter = () => {
  const now = new Date();

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);
  todayEnd.setMilliseconds(-1);

  return [todayStart.toISOString(), todayEnd.toISOString()];
};

const updateDealInList = () => {
  if (
    !isDealsList.value ||
    !dealNotes.value?.[0] ||
    !isNoteOnlyActivity.value
  ) {
    return;
  }

  const {
    created_at,
    id,
    notable_id,
    notable_type,
    stage,
    updated_at,
    visibility_scope,
    visible_in_deal_list,
    comment,
  } = dealNotes.value[0];

  const value = {
    created_at,
    id,
    notable_id,
    notable_type,
    stage,
    updated_at,
    visibility_scope,
    visible_in_deal_list,
    comment,
  };

  commit("applications/updateApplicationDataInList", {
    application_id: activeDealId.value,
    path: "latest_note",
    value,
  });
};

const getActivityAndUpdateDealInTheList = async () => {
  await getActivity();
  updateDealInList();
};

const connectToDealListChannel = () => {
  if (!activeDealId.value) {
    return;
  }

  subscribeToChannelEvents(
    COMMUNICATION_LOGS_APPLICATION_CHANNEL,
    activeDealId.value
  );
};

const leaveDealListChannel = () => {
  if (!activeDealId.value) {
    return;
  }
  leaveChannel(COMMUNICATION_LOGS_APPLICATION_CHANNEL, activeDealId.value);
};

watch(hasNewLogs, async (updatedValue, oldValue) => {
  if (updatedValue && !oldValue) {
    await getActivityAndUpdateDealInTheList();
    hasNewLogs.value = false;
  }
});

watch(
  dealActivityOptions,
  (newValue) => {
    const hasNotesToBeMarkedAsRead = !!notes.value["unread"].length;
    if (hasNotesToBeMarkedAsRead) {
      if (newValue.state !== DealActivityState.Closed) {
        const noteIds = notes.value["unread"].map((note) => note?.id as number);
        notesService.markNotesAsRead(noteIds);
      }
    }
    if (newValue.state === DealActivityState.Closed) {
      setEditNote(null);
    }
  },
  { deep: true }
);

watch(
  activeDealId,
  async (newId) => {
    if (newId && isDealsList.value) {
      await getDealDataAndNotes(newId);
    }
  },
  { immediate: true }
);

watch(
  [isDealActivityPipModeActivated, deal],
  ([isPiPActivated, updatedDeal]) => {
    if (isPiPActivated && updatedDeal?.id && !activePiPDeal.value) {
      activePiPDeal.value = structuredClone(toRaw(deal.value));
      activeNoteDealId.value = activePiPDeal.value?.id;
    }
  },
  { immediate: true }
);

watch(noteTabFilter, (newFilter) => {
  notePinnedFilterValue.value = undefined;
  noteRecentFilter.value = undefined;

  if (newFilter === NoteTab.All) {
    return;
  }

  if (newFilter === NoteTab.Pinned) {
    notePinnedFilterValue.value = user.value?.id;
    return;
  }

  noteRecentFilter.value = setRecentFilter();
});

onMounted(connectToDealListChannel);

onBeforeUnmount(() => {
  if (isDealsList.value && !isPiPModeActionTriggered.value) {
    activeNoteDealId.value = null;
    commit("applications/unsetActive");
  }
  leaveDealListChannel();
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.content-height {
  height: 97%;
}

.deal-notes-input :deep(.autoresizing-text-area) {
  @apply max-h-100 pt-4;
}
</style>
